"use client";

import { useState } from "react";
import { Plus, Trash2, Check, Loader2, Send } from "lucide-react";

/**
 * Quote request form — used on the Pro page and on /contact. Posts to the
 * storefront's own `/api/quotes`, which forwards to the admin API server-side,
 * so the request lands in the back-office "Devis (Pro)" list.
 *
 * Private customers matter as much as garages here: the form opens on
 * "particulier", and the company and VAT fields only appear for a business.
 * Requiring a company name is what used to turn individuals away.
 */
type Line = { productRef: string; qty: number };

export function ProQuoteFormRenderer({
  component,
}: {
  component: { content?: { title?: string; subtitle?: string } };
}) {
  const { title, subtitle } = component.content ?? {};
  const [isCompany, setIsCompany] = useState(false);
  const [company, setCompany] = useState("");
  const [vat, setVat] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — real people never fill this
  const [lines, setLines] = useState<Line[]>([{ productRef: "", qty: 1 }]);
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { productRef: "", qty: 1 }]);
  const removeLine = (i: number) => setLines((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const cleanLines = lines.filter((l) => l.productRef.trim());
    if (cleanLines.length === 0) {
      setError("Indiquez au moins une référence.");
      return;
    }
    if (!name.trim()) {
      setError("Indiquez votre nom pour que nous sachions à qui répondre.");
      return;
    }
    if (isCompany && !company.trim()) {
      setError("Indiquez le nom de votre entreprise.");
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError("Indiquez un e-mail ou un téléphone pour vous recontacter.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isCompany,
          company: isCompany ? company : "",
          vatNumber: isCompany ? vat : "",
          name, email, phone, message, website,
          lines: cleanLines.map((l) => ({ productRef: l.productRef.trim(), quantity: l.qty || 1 })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "L'envoi a échoué.");
      }
      setOk(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'envoi a échoué.");
    } finally {
      setSending(false);
    }
  };

  if (ok) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12 md:px-8">
        <div className="rounded-2xl bg-[#E6F4EA] p-8 text-center ring-1 ring-[#00B862]/20">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#00B862] text-white"><Check className="h-7 w-7" /></span>
          <h2 className="mt-4 text-xl font-bold text-[#121212]" style={{ fontFamily: "var(--font-aleo)" }}>Demande envoyée</h2>
          <p className="mt-2 text-[#4B5563]">Merci, notre équipe pro vous recontacte sous 24h ouvrées avec votre devis.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 md:px-8 md:py-14">
      {title ? (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-[#121212] md:text-3xl" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
          {subtitle ? <p className="mt-2 text-[#6B7280]">{subtitle}</p> : null}
        </div>
      ) : null}

      <form onSubmit={submit} className="apa-card space-y-6 p-6 md:p-8">
        <fieldset>
          <legend className="text-sm font-semibold text-[#121212]">Vous êtes…</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { value: false, label: "Un particulier" },
              { value: true, label: "Une entreprise" },
            ].map((option) => (
              <label
                key={option.label}
                className={`cursor-pointer rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${
                  isCompany === option.value
                    ? "border-[#00B862] bg-[#E6F4EA] text-[#00733D]"
                    : "border-black/15 text-[#4B5563] hover:border-black/30"
                }`}
              >
                <input
                  type="radio"
                  name="customer-type"
                  className="sr-only"
                  checked={isCompany === option.value}
                  onChange={() => setIsCompany(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isCompany ? (
            <>
              <Field label="Entreprise *" value={company} onChange={setCompany} required />
              <Field label="N° TVA" value={vat} onChange={setVat} placeholder="BE0123456789" />
            </>
          ) : null}
          <Field label="Nom du contact *" value={name} onChange={setName} required />
          <Field label="Téléphone" value={phone} onChange={setPhone} type="tel" />
          <Field label="E-mail" value={email} onChange={setEmail} type="email" className="sm:col-span-2 lg:col-span-1" />
        </div>

        {/* Honeypot: hidden from people, irresistible to bots. */}
        <label className="hidden" aria-hidden="true">
          Site web
          <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </label>

        <div>
          <p className="mb-2 text-sm font-semibold text-[#121212]">Références &amp; quantités</p>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={l.productRef}
                  onChange={(e) => setLine(i, { productRef: e.target.value })}
                  placeholder="Référence / OEM (ex. GTB1549V, 144114256R)"
                  className="flex-1 rounded-lg border border-black/15 px-3 py-2.5 text-sm outline-none transition focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/60"
                />
                <input
                  type="number"
                  min={1}
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  className="w-20 rounded-lg border border-black/15 px-3 py-2.5 text-center text-sm outline-none transition focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/60"
                />
                <button type="button" onClick={() => removeLine(i)} aria-label="Retirer"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-[#9CA3AF] hover:bg-[#FBE9E9] hover:text-[#B42318]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#00B862] hover:underline">
            <Plus className="h-4 w-4" /> Ajouter une référence
          </button>
        </div>

        <label className="block text-sm font-medium text-[#121212]">
          Message (optionnel)
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            placeholder="Véhicule, délai souhaité, volume…"
            className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm outline-none transition focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/60" />
        </label>

        {error && <p className="rounded-lg bg-[#FBE9E9] px-3 py-2 text-sm text-[#B42318]">{error}</p>}

        <button type="submit" disabled={sending}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#00B862] px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#00935A] hover:shadow-[var(--shadow-glow)] disabled:bg-[#9CA3AF] disabled:shadow-none">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Envoi…" : "Demander mon devis"}
        </button>
      </form>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, className }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; required?: boolean; className?: string;
}) {
  return (
    <label className={`block text-sm font-medium text-[#121212] ${className ?? ""}`}>
      {label}
      <input type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm outline-none transition focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/60" />
    </label>
  );
}
